import {
  ComponentsLibraryProvider,
  Loader,
  ModalsProvider,
  Notifications,
} from '@qred/components-library';
import { QitUiProvider } from '@qred/qit-ui';
import React, { Suspense } from 'react';
import { Redirect } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import AuthenticatedRoute from '~/components/core/AuthenticatedRoute';
import Spinner from '~/components/shared/Spinner/SpinnerCircular';
import constVars from '~/constants/constVars';
import MainLayout from './components/shared/Layout/MainLayout';
import SecondaryLayout from './components/shared/Layout/SecondaryLayout';
import AppShell from './components/shared/Navigation/AppShell';
import { useMaintenanceFeatureFlagEnabled } from './hooks/useMaintenanceFeatureFlagEnabled';
import useQueryParams from './hooks/useQueryParams';
import BankIntegrationPublic from './pages/BankIntegrationPublic/BankIntegrationPublic';
import CardOffer from './pages/CardOffer/CardOffer';
import PublicCardOffer from './pages/CardOffer/PublicCardOffer';
import ContractSigned from './pages/ContractSigned/ContractSigned';
import CreateCardProgress from './pages/CreateCardProgress/CreateCardProgress';
import CustomerLink from './pages/CustomerLink/CustomerLink';
import Maintenance from './pages/Maintenance/Maintenance';
import { RedirectToOnboardingDomain } from './pages/RedirectToOnboardingDomain/RedirectToOnboardingDomain';
import SetCreditCardPin from './pages/SetCreditCardPin/SetCreditCardPin';
import PublicShippingDetails from './pages/ShippingDetails/PublicShippingDetails';
import TermsAndConditions from './pages/TermsAndConditions/TermsAndConditions';
import ThankYou from './pages/ThankYou/ThankYou';
import ProtectedRoute from './ProtectedRoute';

const Contact = React.lazy(() => import('./pages/Contact/Contact'));
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const Account = React.lazy(() => import('./pages/Account/Account'));
const PageNotFound = React.lazy(
  () => import('./pages/PageNotFound/PageNotFound')
);
const ApplyLoanIncrease = React.lazy(
  () => import('./pages/ApplyLoanIncrease/ApplyLoanIncrease')
);
const Login = React.lazy(() => import('./pages/Login/Login'));
const Document = React.lazy(() => import('./pages/Documents/Documents'));
const Invoices = React.lazy(() => import('./pages/Invoices/Invoices'));
const HelpSupport = React.lazy(() => import('./pages/HelpSupport/HelpSupport'));
const HelpSupportDetail = React.lazy(
  () => import('./pages/HelpSupport/pages/HelpSupportDetail')
);
const Loans = React.lazy(() => import('./pages/Loans/Loans'));

const PROTECTED_ONBOARDING_REDIRECTS = [
  `/${constVars.ROUTE_ONBOARDING_OFFER}/:applicationUuid`,
  `/${constVars.ROUTE_CARD_ONBOARDING_OFFER}/:clientId`,
];

const PUBLIC_ONBOARDING_REDIRECTS = [
  `/${constVars.ROUTE_THANK_YOU_CONTINUE_IN_APP}`,
  `/${constVars.ROUTE_THANK_YOU_WAITING_ADDITIONAL_SIGNER}`,
  `/${constVars.ROUTE_ADDITIONAL_SIGNER_AUTH_OLD}`,
  `/${constVars.ROUTE_ADDITIONAL_SIGNER_AUTH}`,
  `/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER_OLD}`,
  `/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER}`,
  `/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER_OLD}/:type(${constVars.PARAMS_SIGNER_SIGNED_SUCCESS})`,
  `/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER}/:type(${constVars.PARAMS_SIGNER_SIGNED_SUCCESS})`,
  `/${constVars.ROUTE_LOAN_ADDITIONAL_SIGNER_OLD}/:type(${constVars.PARAMS_SIGNER_ALREADY_SIGNED})`,
  `/${constVars.ROUTE_SIGNER_ERROR}`,
  `/${constVars.ROUTE_CARD_ADDITIONAL_SIGNER_OLD}`,
  `/${constVars.ROUTE_CARD_ADDITIONAL_SIGNER}`,
  `/${constVars.ROUTE_ESKAT}`,
  `/${constVars.ROUTE_ESKAT}/redirected`,
  `/${constVars.ROUTE_ONBOARDING}`,
  `/${constVars.ROUTE_BANK_CONNECTION_RESULT}`,
  `/${constVars.ROUTE_BANK_CONNECTION}`,
];

const Routes = () => {
  const queryParams = useQueryParams();
  const isFlagEnabled = useMaintenanceFeatureFlagEnabled();

  if (
    isFlagEnabled &&
    window.location.pathname !== `/${constVars.ROUTE_MAINTENANCE}`
  ) {
    return <Redirect to={`/${constVars.ROUTE_MAINTENANCE}`} />;
  }

  return (
    <Switch>
      <Route exact path={`/${constVars.ROUTE_MAINTENANCE}`}>
        <ComponentsLibraryProvider>
          <SecondaryLayout>
            <Maintenance />
          </SecondaryLayout>
        </ComponentsLibraryProvider>
      </Route>
      <Route exact path={`/${constVars.ROUTE_TERMS_AND_CONDITIONS}`}>
        <TermsAndConditions />
      </Route>
      <ProtectedRoute
        exact
        path={[`/${constVars.ROUTE_CONTACT}`]}
        render={() => (
          <MainLayout legacyStyles>
            <Suspense fallback={<Spinner />}>
              <Contact />
            </Suspense>
          </MainLayout>
        )}
      />
      <ProtectedRoute exact path={`/${constVars.ROUTE_CARD_OFFER}/:companyId`}>
        <MainLayout legacyStyles>
          <CardOffer />
        </MainLayout>
      </ProtectedRoute>
      <ProtectedRoute
        exact
        path={`/${constVars.ROUTE_SET_PIN}/:applicationUuid`}
      >
        <MainLayout legacyStyles>
          <SetCreditCardPin />
        </MainLayout>
      </ProtectedRoute>
      <Route exact path={`/${constVars.ROUTE_PUBLIC_CARD_OFFER}`}>
        <PublicCardOffer />
      </Route>
      <Route path={`/${constVars.ROUTE_THANK_YOU}`}>
        <ThankYou />
      </Route>
      <Route path={`/${constVars.ROUTE_CONTRACT_SIGNED}`}>
        <ContractSigned />
      </Route>
      <Route path={`/${constVars.ROUTE_CARD_CREDIT_LIMIT_INCREASED}`}>
        <ContractSigned />
      </Route>

      <Route
        exact
        path={[
          `/${constVars.ROUTE_CUSTOMER_LINK}/:type(redirected)/:qcode`,
          `/${constVars.ROUTE_CUSTOMER_LINK}/:type(redirected)`,
          `/${constVars.ROUTE_CUSTOMER_LINK}`,
        ]}
      >
        <CustomerLink />
      </Route>
      <Route
        exact
        path={`/${constVars.ROUTE_PUBLIC_SHIPPING_DETAILS}/:applicationUuid`}
      >
        <PublicShippingDetails />
      </Route>
      <Route exact path={`/${constVars.ROUTE_CREATING_CARD}`}>
        <CreateCardProgress />
      </Route>
      <Route exact path={`/:type(${constVars.PARAMS_SIGNED})`}>
        <Redirect to="/" />
      </Route>
      <Route exact path={`/${constVars.ROUTE_KOSMA_PUBLIC}`}>
        <QitUiProvider>
          <ComponentsLibraryProvider>
            <BankIntegrationPublic />
          </ComponentsLibraryProvider>
        </QitUiProvider>
      </Route>

      <Route
        exact
        path="/404"
        render={() => (
          <Suspense fallback={<Spinner />}>
            <ComponentsLibraryProvider>
              <PageNotFound />
            </ComponentsLibraryProvider>
          </Suspense>
        )}
      />

      {/* ONBOARDING DOMAIN ROUTES START */}

      <ProtectedRoute exact path={PROTECTED_ONBOARDING_REDIRECTS}>
        <RedirectToOnboardingDomain />
      </ProtectedRoute>

      <Route exact path={PUBLIC_ONBOARDING_REDIRECTS}>
        <RedirectToOnboardingDomain />
      </Route>

      {/* ONBOARDING DOMAIN ROUTES END */}

      <ComponentsLibraryProvider>
        <ModalsProvider>
          <Switch>
            <Route exact path={`/${constVars.ROUTE_LOGIN}`}>
              {queryParams.get('code') && queryParams.get('state') ? (
                <Loader />
              ) : (
                <Suspense fallback={<Loader />}>
                  <Login />
                </Suspense>
              )}
            </Route>
            <AuthenticatedRoute>
              <AppShell>
                <Route exact path={`/`}>
                  <Suspense fallback={<Loader />}>
                    <Dashboard />
                  </Suspense>
                </Route>
                <Route exact path={`/${constVars.ROUTE_PROFILE}`}>
                  <Redirect to={`/${constVars.ROUTE_ACCOUNT}`} />
                </Route>
                <Route exact path={`/${constVars.ROUTE_ACCOUNT}`}>
                  <Suspense fallback={<Loader />}>
                    <Account />
                  </Suspense>
                </Route>
                <Route exact path={`/${constVars.ROUTE_DOCUMENTS}`}>
                  <Suspense fallback={<Loader />}>
                    <Document />
                  </Suspense>
                </Route>
                <Route exact path={`/${constVars.ROUTE_APPLYING_LOAN}`}>
                  <Suspense fallback={<Loader />}>
                    <ApplyLoanIncrease />
                  </Suspense>
                </Route>
                <Route exact path={`/${constVars.ROUTE_INVOICES}`}>
                  <Suspense fallback={<Loader />}>
                    <Invoices />
                  </Suspense>
                </Route>
                <Route exact path={`/${constVars.ROUTE_HELP_AND_SUPPORT}`}>
                  <Suspense fallback={<Loader />}>
                    <HelpSupport />
                  </Suspense>
                </Route>
                <Route exact path={`/${constVars.ROUTE_LOANS}`}>
                  <Suspense fallback={<Loader />}>
                    <Loans />
                  </Suspense>
                </Route>
                <Route
                  exact
                  path={`/${constVars.ROUTE_HELP_AND_SUPPORT}/:category`}
                >
                  <Suspense fallback={<Loader />}>
                    <HelpSupportDetail />
                  </Suspense>
                </Route>
              </AppShell>
            </AuthenticatedRoute>
          </Switch>
        </ModalsProvider>
        <Notifications containerWidth="24em" />
      </ComponentsLibraryProvider>
      <Route
        exact
        path="*"
        render={() => (
          <Suspense fallback={<Spinner />}>
            <PageNotFound />
          </Suspense>
        )}
      />
    </Switch>
  );
};

export default Routes;
